import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { identityFieldKey } from '@/features/entity-resolution/identityAssessment'
import type { ResolutionSummary } from '@/services/api/entity-resolution-assistant'
import { cn } from '@/utils/cn'
import { identityChecksAvailable } from './resolution-identity'

export function ResolutionIdentitySummary({ summary }: { summary: ResolutionSummary }) {
  const { t } = useTranslation()
  const checks = summary.identity_checks
  if (!checks || !identityChecksAvailable(summary))
    return <p role="status">{t('resolutionAssistant.identityRefresh')}</p>
  const blocked = checks.conflict_groups + checks.review_groups
  return (
    <Card className={cn('gap-0 py-3', checks.conflict_groups > 0 && 'border-destructive/40')}>
      <CardContent className="space-y-1 px-3 text-xs">
        <p className="font-medium">{t('resolutionAssistant.identityTitle')}</p>
        <p>{t('resolutionAssistant.identityCounts', { ...checks })}</p>
        {blocked > 0 && <p>{t('resolutionAssistant.identityExcluded')}</p>}
        <IdentityFieldCounts kind="conflicts" fields={checks.conflict_fields} />
        <IdentityFieldCounts kind="review" fields={checks.review_fields} />
        <p>{t('resolutionAssistant.identityBasis')}</p>
      </CardContent>
    </Card>
  )
}

function IdentityFieldCounts({
  kind,
  fields,
}: {
  kind: 'conflicts' | 'review'
  fields: Record<string, number>
}) {
  const { t } = useTranslation()
  const entries = Object.entries(fields).filter(([, count]) => count > 0)
  if (!entries.length) return null
  return (
    <p>
      {t(`resolutionIdentity.fieldGroups.${kind}`)}:{' '}
      {entries.map(([field, count]) => `${t(identityFieldKey(field))}: ${count}`).join(' · ')}
    </p>
  )
}
